import React, { useState, memo, forwardRef, useImperativeHandle, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { apiCall } from '../utils/api';
import { GoogleAuthProvider } from 'firebase/auth';
import ConsentModal from './ConsentModal';
import '../styles/styles.css';

const SignInScreen = forwardRef(({ onSignIn, onLoadingChange, onReadyChange, showInlineButton = true }, ref) => {
    const { signInWithGoogle, setAuthData, clearAuthData } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showConsentModal, setShowConsentModal] = useState(false);
    const [userData, setUserData] = useState(null);
    const navigate = useNavigate();
    const productId = 2; // Hardcoded for now, adjust as needed

    const checkConsentStatus = useCallback(async (userId, accessToken) => {
        try {
            const response = await apiCall(
                `/users/${userId}/product-consent/${productId}/status`,
                'GET',
                null,
                true,
                userId,
                accessToken
            );
            console.log('[SignInScreen] Consent status check');
            return response.has_consented;
        } catch (err) {
            console.error('[SignInScreen] Error checking consent');
            throw err;
        }
    }, [productId]);

    const handleAcceptConsent = async () => {
        if (!userData || !userData.user_id || !userData.accessToken) {
            setError('User data not available for consent');
            return;
        }
        try {
            await apiCall(
                `/users/${userData.user_id}/accept-product-consent/${productId}`,
                'POST',
                {},
                true,
                userData.user_id,
                userData.accessToken
            );
            console.log('[SignInScreen] Consent accepted');
            setShowConsentModal(false);
            onSignIn(userData);
            navigate('/');
        } catch (err) {
            console.error('[SignInScreen] Error accepting consent');
            if (err.message.includes('400')) {
                setShowConsentModal(false);
                onSignIn(userData);
                navigate('/');
            } else {
                setError('Failed to accept consent');
                handleDeclineConsent();
            }
        }
    };

    const handleDeclineConsent = useCallback(async () => {
        console.log('[SignInScreen] User declined consent, logging out');
        await clearAuthData();
        setShowConsentModal(false);
        setUserData(null);
        setError('You must accept the terms to use the application');
        navigate('/');
    }, [clearAuthData, navigate]);

    const onGoogleSignInSuccess = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await signInWithGoogle();
            if (!result || !result.user) {
                throw new Error('Invalid Google Sign-In response');
            }

            const user = result.user;
            const credential = GoogleAuthProvider.credentialFromResult(result);
            const idToken = credential.idToken;
            if (!idToken) {
                throw new Error('Google ID token not found');
            }

           console.log('[SignInScreen] Google Sign-In Details:', {
                idToken: idToken,
                userEmail: user.email,
                credential: credential
            }); 
            const userInfoResponse = await fetch('https://www.googleapis.com/userinfo/v2/me', {
                headers: { Authorization: `Bearer ${credential.accessToken}` },
            });
            const userInfo = await userInfoResponse.json();

            const { access_token: accessToken, supabase_refresh_token: refreshToken } = await apiCall(
                '/auth/login',
                'POST',
                {
                    google_id_token: idToken,
                    product_id: productId,
                    first_name: userInfo.given_name || '',
                    last_name: userInfo.family_name || ''
                },
                true
            );

            if (!accessToken) {
                throw new Error('No access token received');
            }

            const tokenPayload = JSON.parse(atob(accessToken.split('.')[1]));
            const userDataTemp = {
                user_id: Number(tokenPayload.user_id),
                firstname: userInfo.given_name || (user.displayName ? user.displayName.split(' ')[0] : ''),
                lastname: userInfo.family_name || (user.displayName ? user.displayName.split(' ')[1] || '' : ''),
                email: user.email || '',
                accessToken,
                refreshToken,
            };

            setUserData(userDataTemp);

            const hasConsented = await checkConsentStatus(userDataTemp.user_id, accessToken);
            if (hasConsented) {
                setAuthData(
                    userDataTemp.user_id,
                    `${userDataTemp.firstname} ${userDataTemp.lastname}`,
                    accessToken,
                    refreshToken,
                    userDataTemp.email,
                    () => {
                        onSignIn(userDataTemp);
                        navigate('/');
                    }
                );
            } else {
                setShowConsentModal(true);
            }
        } catch (err) {
            console.error('[SignInScreen] Authentication error occurred');
            setError('Authentication failed. Please try again or contact support.');
        } finally {
            setLoading(false);
        }
    }, [signInWithGoogle, productId, checkConsentStatus, setAuthData, onSignIn, navigate, handleDeclineConsent]);

    useImperativeHandle(ref, () => ({
        triggerSignIn: onGoogleSignInSuccess,
    }), [onGoogleSignInSuccess]);

    useEffect(() => {
        if (onLoadingChange) {
            onLoadingChange(loading);
        }
    }, [loading, onLoadingChange]);

    useEffect(() => {
        return () => {
            if (onLoadingChange) {
                onLoadingChange(false);
            }
        };
    }, [onLoadingChange]);

    useEffect(() => {
        if (onReadyChange) {
            onReadyChange(true);
            return () => onReadyChange(false);
        }
        return undefined;
    }, [onReadyChange]);

    return (
        <div className="signin-page">
            <div className="signin-container" aria-label="Sign in">

                {error && <p className="error-text">{error}</p>}

                {/* Centered Button / Spinner */}
                {(loading || showInlineButton) && (
                    <div className="signin-button-wrapper">
                        {loading ? (
                            <div className="loading-container">
                                <div className="spinner"></div>
                                <p>Processing...</p>
                            </div>
                        ) : (
                            showInlineButton && (
                                <button
                                    className="sign-in-button button google-sign-in-button"
                                    onClick={onGoogleSignInSuccess}
                                    disabled={loading}
                                >
                                    Sign In with Google
                                </button>
                            )
                        )}
                    </div>
                )}
            </div>

            <main className="landing-main" aria-labelledby="hero-title">
                <section id="hero" className="landing-section hero-section">
                    <div className="section-container hero-content">
                        <h1 id="hero-title" className="hero-title">
                            Empowering Learning with AI {String.fromCodePoint(0x1f9e0)}
                        </h1>
                        <p className="hero-subtitle">
                            QuizAcer transforms any study material into a personalized, adaptive learning experience, helping students and professionals master content faster and more effectively.
                        </p>
                        <button
                            className="sign-in-button button hero-cta-button"
                            onClick={onGoogleSignInSuccess}
                            disabled={loading}
                            type="button"
                        >
                            Try it out
                        </button>
                    </div>
                </section>

                <section id="problem" className="landing-section">
                    <div className="section-container">
                        <div className="section-header">
                            <h2 className="section-title">
                                The Challenge of Effective Study {String.fromCodePoint(0x1f605)}
                            </h2>
                            <p className="section-subtitle">
                                Modern learning is inefficient. Students and professionals are drowning in dense materials, with no easy way to test their knowledge and identify gaps.
                            </p>
                        </div>
                        <div className="card-grid">
                            <div className="info-card" aria-labelledby="card-overload-title">
                                <div className="info-card-icon" role="img" aria-label="Information overload">
                                    {String.fromCodePoint(0x1f4da)}
                                </div>
                                <h3 id="card-overload-title" className="info-card-title">Information Overload</h3>
                                <p className="info-card-text">Dense textbooks and long study guides are overwhelming and difficult to digest.</p>
                            </div>
                            <div className="info-card" aria-labelledby="card-time-title">
                                <div className="info-card-icon" role="img" aria-label="Time-consuming preparation">
                                    {String.fromCodePoint(0x23f3)}
                                </div>
                                <h3 id="card-time-title" className="info-card-title">Time-Consuming Prep</h3>
                                <p className="info-card-text">Creating effective quizzes and study materials manually takes hours of valuable time.</p>
                            </div>
                            <div className="info-card" aria-labelledby="card-fit-title">
                                <div className="info-card-icon" role="img" aria-label="Target">
                                    {String.fromCodePoint(0x1f3af)}
                                </div>
                                <h3 id="card-fit-title" className="info-card-title">One-Size-Fits-All</h3>
                                <p className="info-card-text">Traditional study methods fail to address individual learning needs and weaknesses.</p>
                            </div>
                            <div className="info-card" aria-labelledby="card-stress-title">
                                <div className="info-card-icon" role="img" aria-label="Stressed face">
                                    {String.fromCodePoint(0x1f630)}
                                </div>
                                <h3 id="card-stress-title" className="info-card-title">Wasted Effort &amp; Anxiety</h3>
                                <p className="info-card-text">Inefficient studying leads to wasted time and high stress before important exams.</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section id="solution" className="landing-section solution-section">
                    <div className="section-container">
                        <div className="section-header">
                            <h2 className="section-title">Our Solution: QuizAcer {String.fromCodePoint(0x1f4a1)}</h2>
                            <p className="section-subtitle">
                                We turn static documents into dynamic, interactive learning tools in just a few simple steps. This section explains how our AI-powered platform makes learning seamless and efficient.
                            </p>
                        </div>
                        <div className="steps-wrapper" aria-label="How QuizAcer works">
                            <div className="step-grid">
                                <div className="step-card">
                                    <div className="step-circle" aria-hidden="true">1</div>
                                    <h3 className="step-title">Upload PDF</h3>
                                    <p className="step-text">Instantly upload any document, from textbooks to research papers.</p>
                                </div>
                                <div className="step-card">
                                    <div className="step-circle" aria-hidden="true">2</div>
                                    <h3 className="step-title">Generate Quiz</h3>
                                    <p className="step-text">Our AI analyzes the content and generates relevant multiple-choice questions.</p>
                                </div>
                                <div className="step-card">
                                    <div className="step-circle" aria-hidden="true">3</div>
                                    <h3 className="step-title">Take Quiz</h3>
                                    <p className="step-text">Engage with the material through an interactive, user-friendly quiz interface.</p>
                                </div>
                                <div className="step-card">
                                    <div className="step-circle" aria-hidden="true">4</div>
                                    <h3 className="step-title">Get Insights</h3>
                                    <p className="step-text">Receive immediate feedback and personalized recommendations for improvement.</p>
                                </div>
                            </div>
                        </div>
                        <div className="solution-cta">
                            <button
                                className="sign-in-button button hero-cta-button"
                                onClick={onGoogleSignInSuccess}
                                disabled={loading}
                                type="button"
                            >
                                Get Started
                            </button>
                        </div>
                    </div>
                </section>
            </main>

            <ConsentModal
                open={showConsentModal}
                onAccept={handleAcceptConsent}
                onCancel={handleDeclineConsent}
            />
        </div>
    );
});

export default memo(SignInScreen);